import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { CommonType } from "../../types";

type PayloadCardProps = {
  rawBaseData: string;
  rawData: string;
  parsedType: CommonType[];
  selectedParseType: CommonType | null;
  selectedConvertType: CommonType | null;
  onParseTypeChange: (value: CommonType) => void;
  onConvertTypeChange: (value: CommonType) => void;
  selectedParseTypeId: number | null;
  selectedConvertTypeId: number | null;
};

const A = 18;
const H = 82;

const desktopClip = {
  first:  `polygon(0 0, calc(100% - ${A}px) 0, 100% 50%, calc(100% - ${A}px) 100%, 0 100%)`,
  middle: `polygon(0 0, calc(100% - ${A}px) 0, 100% 50%, calc(100% - ${A}px) 100%, 0 100%, ${A}px 50%)`,
  last:   `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${A}px 50%)`,
};

const labelSx = {
  position: "absolute" as const,
  top: 6,
  fontSize: 11,
  fontWeight: 600,
  color: "#444",
};

const inputSx = {
  "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: "5px" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiInputBase-input": { fontSize: 13 },
};

const PayloadCard: React.FC<PayloadCardProps> = ({
  rawBaseData,
  rawData,
  parsedType,
  selectedParseType,
  selectedConvertType,
  onParseTypeChange,
  onConvertTypeChange,
  selectedParseTypeId: _selectedParseTypeId,
  selectedConvertTypeId: _selectedConvertTypeId,
}) => (
  <Box sx={{
    mx: 2, mt: 2, mb: 1,
    borderRadius: 2,
    py: "6px",
    px: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  }}>

    {/* Row 1 */}
    <Box sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      gap: "6px",
    }}>

      {/* BÖLÜM 1 — Orj. Payload */}
      <Box sx={{
        position: "relative",
        flex: { md: 5 },
        height: H,
        bgcolor: "#3A9AD533",
        clipPath: { xs: "none", md: desktopClip.first },
        borderRadius: { xs: "6px", md: 0 },
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        pl: "10px",
        pr: { xs: "10px", md: `${A + 10}px` },
      }}>
        <Typography sx={labelSx}>Orj. Payload</Typography>
        <TextField
          fullWidth size="small"
          value={rawBaseData}
          disabled
          sx={inputSx}
        />
      </Box>

      {/* BÖLÜM 2 — Parse + Convert */}
      <Box sx={{
        position: "relative",
        flex: { md: 4 },
        height: H,
        bgcolor: "#3A9AD580",
        clipPath: { xs: "none", md: desktopClip.middle },
        borderRadius: { xs: "6px", md: 0 },
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        pl: { xs: "10px", md: `${A + 10}px` },
        pr: { xs: "10px", md: `${A + 10}px` },
      }}>
        <Box sx={{ position: "absolute", top: 6, left: { xs: "10px", md: `${A + 10}px` }, right: { xs: "10px", md: `${A + 10}px` }, display: "flex", gap: "6px" }}>
          <Typography sx={{ ...labelSx, position: "static", flex: 1 }}>Parse Type</Typography>
          <Typography sx={{ ...labelSx, position: "static", flex: 1 }}>Convert Type</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "6px", width: "100%" }}>
          <Autocomplete
            sx={{ flex: 1 }}
            size="small"
            options={parsedType}
            value={selectedParseType ?? undefined}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            getOptionLabel={(o) => o.name || ""}
            onChange={(_, value) => value && onParseTypeChange(value)}
            disableClearable
            renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />}
          />
          <Autocomplete
            sx={{ flex: 1 }}
            size="small"
            options={parsedType}
            value={selectedConvertType ?? undefined}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            getOptionLabel={(o) => o.name || ""}
            onChange={(_, value) => value && onConvertTypeChange(value)}
            disableClearable
            renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />}
          />
        </Box>
      </Box>
    </Box>

    {/* BÖLÜM 3 — Parsable Payload */}
    <Box sx={{
      position: "relative",
      height: H,
      bgcolor: "#3A9AD5BF",
      clipPath: { xs: "none", md: desktopClip.last },
      borderRadius: { xs: "6px", md: 0 },
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      pl: { xs: "10px", md: `${A + 10}px` },
      pr: "22px",
    }}>
      <Typography sx={labelSx}>Parsable Payload</Typography>
      <TextField
        fullWidth size="small"
        value={rawData}
        sx={{
          ...inputSx,
          "& .MuiInputBase-input": { fontSize: 15, fontWeight: 500, letterSpacing: "0.03em" },
        }}
      />
    </Box>
  </Box>
);

export default PayloadCard;
